/* Copyright (c) 2020-present CloudIO Inc */

import {
  atom,
  useStateXAtomSetter,
  useStateXAtomValue,
} from '@cloudio-saas/statex';
import { useMonaco } from '@monaco-editor/react';
import { useMonacoTypes } from 'custom/CodeInput';
import { useAppID } from 'designer/atoms';
import { useEffect, useState } from 'react';
import useUser from 'root/useUser';
import { getURLQueryStringToPost } from 'utils/url';
import { getBaseUrl, getUrl } from '../../../../config/config';
import { BUILD_DATE } from '../../../Header/About';
import {
  Script,
  scripts,
  testScriptTypests,
  uiScriptTypests,
} from './constants';

export function clearDBTypes() {
  delete ts_types['@cloudio-saas/datasource-types/index.d.ts'];
}

const dsTypesLoadedAtom = atom({
  path: ['transient', 'dsTypesLoaded'],
  defaultValue: false,
});

export function useCodeEditorTypes(
  ds?: string,
  scriptType?: Omit<Script['attribute'], 'pageController' | 'wf'>,
  extraScript?: string,
): void {
  const [types, setTypes] = useState('');
  const loaded = useStateXAtomValue(dsTypesLoadedAtom);

  // useMonacoTypes({ types, typesName: 'ds' });

  useEffect(() => {
    if (!loaded) return;
    const script = scripts.find((s) => s.attribute === scriptType);
    const tdts = `import {
    Row,
    DBRow,
    DB,
    WF,
    Azure,
    Cache,
    Session,
    CurrentDataSource,
    FunctionRequest,
    FunctionResponse,
    Console,
    Logger,
    Utils,
    ExtDataSourceAttribute,
    Query } from '@cloudio-saas/datasource-types';
${
  ds
    ? `import { ${ds} } from '@cloudio-saas/datasource-types';
type A = ${ds};`
    : 'type A = Record<string, unknown>;'
}
${script ? script.queryVars : ''}
${extraScript ?? ''}
`;

    setTypes(tdts);
  }, [ds, extraScript, loaded, scriptType]);

  const monaco = useMonaco();
  useEffect(() => {
    if (!(monaco && types)) return;
    try {
      const disposable =
        monaco.languages.typescript.typescriptDefaults.addExtraLib(
          types,
          `${MONACO_LIB_PREFIX}io-ds/index.d.ts`,
        );
      return () => {
        disposable.dispose();
      };
    } catch (e) {
      console.error(e);
    }
  }, [monaco, types]);
}

const ts_types: Record<string, string> = {};
export const MONACO_LIB_PREFIX = 'file:///node_modules/';

export function useTypesLoader(
  nodeModuleFileName: string,
  fileName: string,
): void {
  const [types, setTypes] = useState(ts_types[nodeModuleFileName] ?? '');
  const monaco = useMonaco();
  const appUid = useAppID();
  const user = useUser();
  const setLoaded = useStateXAtomSetter(dsTypesLoadedAtom);

  useEffect(() => {
    if (!monaco || types) return;
    async function load() {
      try {
        let text = '';
        if (fileName === 'custom-types.d.ts') {
          const resp2 = await fetch(
            `${getUrl()}fn/cloudio/cloudio_custom_types?${getURLQueryStringToPost(
              user,
            )}`,
            {
              credentials: 'include',
            },
          );
          text = await resp2.text();
        } else {
          const resp = await fetch(
            `${getBaseUrl()}types/${fileName}?v=${BUILD_DATE}`,
            {
              method: 'GET',
              credentials: 'include',
            },
          );
          text = await resp.text();
          if (fileName === 'ds-types.d.ts') {
            const resp2 = await fetch(
              `${getUrl()}fn/${appUid}/cloudio_ds_types?${getURLQueryStringToPost(
                user,
              )}`,
              {
                credentials: 'include',
              },
            );
            const text2 = await resp2.text();
            text = text.replace(
              'interface AllDatasources {',
              text2.substring(0, text2.length - 1),
            );
          }
        }
        ts_types[nodeModuleFileName] = text;
        setTypes(text);
      } catch (e) {
        console.error(`Error loading ${fileName}`);
        console.error(e);
        setTypes('type x = string;');
      }
    }
    load();
  }, [appUid, fileName, monaco, nodeModuleFileName, types, user]);

  useEffect(() => {
    if (!(monaco && types && nodeModuleFileName)) return;
    try {
      const disposable =
        monaco.languages.typescript.typescriptDefaults.addExtraLib(
          types,
          `${MONACO_LIB_PREFIX}${nodeModuleFileName}`,
        );
      if (fileName === 'ds-types.d.ts') {
        setLoaded(true);
      }
      return () => {
        disposable.dispose();
      };
    } catch (e) {
      console.error(e);
    }
  }, [fileName, monaco, nodeModuleFileName, setLoaded, types]);
}

export function useControllerCodeEditorTypes(): void {
  const [types, setTypes] = useState('');

  useTypesLoader('csstype/index.d.ts', 'csstype.d.ts');
  useTypesLoader('prop-types/index.d.ts', 'prop-types.d.ts');
  useTypesLoader('react/index.d.ts', 'react.d.ts');
  useTypesLoader('date-fns/index.d.ts', 'dateFns.d.ts');
  useTypesLoader('@mui/material/index.d.ts', 'mui.d.ts');
  useTypesLoader('@mui/styles/index.d.ts', 'mui-styles.d.ts');
  useTypesLoader('@mui/lab/index.d.ts', 'mui-lab.d.ts');
  useTypesLoader('@cloudio-saas/custom-types/index.d.ts', 'custom-types.d.ts');
  useTypesLoader('cloudio/index.d.ts', 'cloudio.d.ts');
  useTypesLoader('platform/index.d.ts', 'platform.d.ts');
  useTypesLoader('@cloudio-saas/mutx/index.d.ts', 'mutx.d.ts');
  useTypesLoader('@cloudio-saas/statex/index.d.ts', 'statex.d.ts');
  useTypesLoader('@cloudio-saas/datasource-types/index.d.ts', 'ds-types.d.ts');
  useTypesLoader('chart.js/index.d.ts', 'chartjs.d.ts');
  useTypesLoader('@types/d3/index.d.ts', 'd3.d.ts');

  useMonacoTypes({ types, typesName: 'ds', dom: true });

  useEffect(() => {
    const tdts = `
    declare module "clsx";
    declare module '@types/node';
    declare module "framer-motion";
    declare module "ulid";
    declare module "uuid";
`;
    setTypes(tdts);
  }, []);
}

export function useBackendCodeEditorTypes(): void {
  const [types, setTypes] = useState('');

  useTypesLoader('date-fns/index.d.ts', 'dateFns.d.ts');
  useTypesLoader('lodash/index.d.ts', 'lodash.d.ts');
  useTypesLoader('crypto-js/index.d.ts', 'crypto-js.d.ts');
  useTypesLoader('@cloudio-saas/custom-types/index.d.ts', 'custom-types.d.ts');
  useTypesLoader('@cloudio-saas/datasource-types/index.d.ts', 'ds-types.d.ts');
  useTypesLoader('db/index.d.ts', 'db.d.ts');
  useMonacoTypes({ types, typesName: 'ds', dom: false });

  useEffect(() => {
    const tdts = `
    declare module "sf";
    declare module "patch";
`;
    setTypes(tdts);
  }, []);
}

export function useTestScriptCodeEditorTypes(): void {
  useTypesLoader('date-fns/index.d.ts', 'dateFns.d.ts');
  useTypesLoader('@cloudio-saas/datasource-types/index.d.ts', 'ds-types.d.ts');
  useTypesLoader('chai/index.d.ts', 'chai.d.ts');
  useTypesLoader('@cloudio-saas/custom-types/index.d.ts', 'custom-types.d.ts');
  useTypesLoader('cloudio/index.d.ts', 'cloudio.d.ts');
  useMonacoTypes({ types: testScriptTypests, typesName: 'ds', dom: true });
}

export function useUIScriptCodeEditorTypes(): void {
  useTypesLoader('date-fns/index.d.ts', 'dateFns.d.ts');
  useTypesLoader('@cloudio-saas/datasource-types/index.d.ts', 'ds-types.d.ts');
  useTypesLoader('@cloudio-saas/custom-types/index.d.ts', 'custom-types.d.ts');
  useTypesLoader('cloudio/index.d.ts', 'cloudio.d.ts');
  useMonacoTypes({ types: uiScriptTypests, typesName: 'ds', dom: true });
}
